import * as THREE from 'three';
import { Entity } from './Entity.js';

/**
 * @typedef {Object} TreeOptions
 * @property {THREE.Vector3} [position] - Initial position
 */

/**
 * A scratchable tree entity. Made of a trunk and cone canopy.
 * Cats can scratch these for points.
 */
export class Tree extends Entity {
    /** @type {'normal' | 'burning' | 'scorched'} */
    #burnState = 'normal';

    /** @type {number} */
    #burnTimeRemaining = 0;

    /** @type {boolean} */
    #scratchAwarded = false;

    /** @type {THREE.Mesh | null} */
    #trunk = null;

    /** @type {THREE.Mesh | null} */
    #canopy = null;

    /**
     * @param {TreeOptions} [options]
     */
    constructor(options = {}) {
        super({
            position: options.position,
            collisionRadius: 2.0,
            collidable: true,
        });

        this.#buildMesh();
    }

    /**
     * Whether points have already been awarded for scratching this tree
     * @returns {boolean}
     */
    get scratchAwarded() {
        return this.#scratchAwarded;
    }

    /**
     * Whether this tree has been burned
     * @returns {boolean}
     */
    get burned() {
        return this.#burnState !== 'normal';
    }

    /**
     * Whether this tree is actively burning
     * @returns {boolean}
     */
    get burning() {
        return this.#burnState === 'burning';
    }

    /**
     * Whether this tree is fully scorched
     * @returns {boolean}
     */
    get scorched() {
        return this.#burnState === 'scorched';
    }

    /**
     * Award scratch points (only once per tree)
     * @returns {boolean} True if points were awarded, false if already scratched
     */
    awardScratch() {
        if (this.#burnState !== 'normal') return false;
        if (this.#scratchAwarded) return false;
        this.#scratchAwarded = true;
        return true;
    }

    /**
     * Burn this tree (idempotent)
     * @returns {boolean} True when burn was newly applied
     */
    burn() {
        if (this.#burnState !== 'normal') {
            return false;
        }

        this.#burnState = 'burning';
        this.#burnTimeRemaining = 8.0;
        this.#scratchAwarded = true;

        if (this.#trunk && this.#trunk.material instanceof THREE.MeshStandardMaterial) {
            this.#trunk.material.emissive.setHex(0xff4a00);
            this.#trunk.material.emissiveIntensity = 0.35;
        }

        if (this.#canopy && this.#canopy.material instanceof THREE.MeshStandardMaterial) {
            this.#canopy.material.emissive.setHex(0xff6100);
            this.#canopy.material.emissiveIntensity = 0.25;
        }

        return true;
    }

    /**
     * Update burning progression
     * @param {number} delta
     */
    update(delta) {
        if (this.#burnState !== 'burning') {
            return;
        }

        this.#burnTimeRemaining -= delta;

        const flicker = 0.18 + Math.abs(Math.sin(Date.now() * 0.02)) * 0.26;
        if (this.#trunk && this.#trunk.material instanceof THREE.MeshStandardMaterial) {
            this.#trunk.material.emissiveIntensity = flicker;
        }
        if (this.#canopy && this.#canopy.material instanceof THREE.MeshStandardMaterial) {
            this.#canopy.material.emissiveIntensity = flicker * 0.8;
        }

        if (this.#burnTimeRemaining > 0) {
            return;
        }

        this.#burnState = 'scorched';

        if (this.#trunk && this.#trunk.material instanceof THREE.MeshStandardMaterial) {
            this.#trunk.material.color.setHex(0x1e1e1e);
            this.#trunk.material.emissive.setHex(0x220700);
            this.#trunk.material.emissiveIntensity = 0.12;
        }

        if (this.#canopy && this.#canopy.material instanceof THREE.MeshStandardMaterial) {
            this.#canopy.material.color.setHex(0x2b2b2b);
            this.#canopy.material.emissive.setHex(0x1a0600);
            this.#canopy.material.emissiveIntensity = 0.08;
        }
    }

    /**
     * Build the tree mesh (trunk + canopy)
     */
    #buildMesh() {
        // Trunk
        const trunkGeometry = new THREE.CylinderGeometry(0.5, 0.7, 5, 8);
        const trunkMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        trunk.position.y = 2.5;
        trunk.castShadow = true;
        trunk.receiveShadow = true;
        this.mesh.add(trunk);
        this.#trunk = trunk;

        // Canopy (leaves)
        const canopyGeometry = new THREE.ConeGeometry(3, 6, 8);
        const canopyMaterial = new THREE.MeshStandardMaterial({ color: 0x228B22 });
        const canopy = new THREE.Mesh(canopyGeometry, canopyMaterial);
        canopy.position.y = 7;
        canopy.castShadow = true;
        canopy.receiveShadow = true;
        this.mesh.add(canopy);
        this.#canopy = canopy;
    }
}
